import Box from '@mui/material/Box';
import { useInRouterContext, useLocation } from 'react-router-dom';

// Original SVG artwork authored for Camptain. No stock artwork, logos or external assets.
type Motif = 'records' | 'book' | 'building' | 'people' | 'coins' | 'calendar' | 'bus' | 'medical' | 'sports' | 'message';
const MENU_ART: Record<string, { label: string; motif: Motif }> = {
  administration: { label: 'Administration', motif: 'records' },
  'audit-logs': { label: 'Audit Logs', motif: 'records' },
  'education-boards': { label: 'Education Boards', motif: 'book' },
  'global-settings': { label: 'Global Settings', motif: 'records' },
  'document-permissions': { label: 'Document Permissions', motif: 'records' },
  grading: { label: 'Grading', motif: 'records' },
  classes: { label: 'Classes', motif: 'book' },
  assignments: { label: 'Assignments', motif: 'records' },
  exams: { label: 'Exams', motif: 'records' },
  performance: { label: 'Performance', motif: 'records' },
  certificates: { label: 'Certificates', motif: 'records' },
  school: { label: 'School', motif: 'building' },
  academics: { label: 'Academics', motif: 'book' },
  library: { label: 'Library', motif: 'book' },
  people: { label: 'People', motif: 'people' },
  staff: { label: 'Staff', motif: 'people' },
  students: { label: 'Students', motif: 'people' },
  admissions: { label: 'Admissions', motif: 'records' },
  fees: { label: 'Finance and fees', motif: 'coins' },
  timetable: { label: 'Timetable', motif: 'calendar' },
  attendance: { label: 'Attendance', motif: 'calendar' },
  examination: { label: 'Examinations', motif: 'records' },
  assessment: { label: 'Assessments', motif: 'records' },
  hostel: { label: 'Hostel', motif: 'building' },
  facility: { label: 'Facilities', motif: 'building' },
  health: { label: 'Health', motif: 'medical' },
  medical: { label: 'Medical', motif: 'medical' },
  sports: { label: 'Sports', motif: 'sports' },
  transport: { label: 'Transport', motif: 'bus' },
  communication: { label: 'Communication', motif: 'message' },
  messages: { label: 'Messages', motif: 'message' },
};

function MenuMotif({ motif }: { motif: Motif }) {
  switch (motif) {
    case 'book': return <><path d="M4 9Q14 5 24 10Q34 5 44 9V39Q34 35 24 40Q14 35 4 39Z" /><path d="M24 10V40M10 16H18M30 16H38M10 23H18M30 23H38" /></>;
    case 'building': return <><path d="M3 20L24 6L45 20M8 19V41H40V19M20 41V29H28V41M14 22H16M32 22H34" /><path d="M20 17H28" /></>;
    case 'people': return <><circle cx="24" cy="14" r="7" /><path d="M11 40V34Q11 24 24 24Q37 24 37 34V40M5 23Q0 30 3 38M43 23Q48 30 45 38" /></>;
    case 'coins': return <><ellipse cx="20" cy="12" rx="15" ry="6" /><path d="M5 12V20Q20 32 35 20V12M5 20V28Q16 36 27 31" /><circle cx="34" cy="34" r="11" /><path d="M34 28V40M38 29H32Q28 33 34 34Q40 36 35 39H30" /></>;
    case 'calendar': return <><rect x="5" y="10" width="38" height="32" rx="4" /><path d="M5 20H43M14 5V15M34 5V15M14 28H18M26 28H30M14 35H18M26 35H30" /></>;
    case 'bus': return <><rect x="4" y="10" width="40" height="28" rx="6" /><path d="M4 27H44M13 10V27M26 10V27M37 10V27M9 33H12M36 33H39" /><circle cx="13" cy="39" r="4" /><circle cx="35" cy="39" r="4" /></>;
    case 'medical': return <><rect x="5" y="13" width="38" height="30" rx="5" /><path d="M16 13V7H32V13M20 21H28V27H34V35H28V41H20V35H14V27H20Z" /></>;
    case 'sports': return <><circle cx="24" cy="24" r="19" /><path d="M5 24H43M24 5V43M11 10Q34 24 11 38M37 10Q14 24 37 38" /></>;
    case 'message': return <><rect x="4" y="10" width="40" height="28" rx="4" /><path d="M5 12L24 26L43 12M5 36L17 24M43 36L31 24" /></>;
    default: return <><path d="M10 5H31L40 14V43H10Z M31 5V15H40M17 23H32M17 30H32M17 37H26" /><path d="M5 11V46H30" /></>;
  }
}

function Illustration({ pathname, compact }: { pathname: string; compact: boolean }) {
  // The first segment identifies the role; the next segment identifies its menu.
  const segments = pathname.split('/').filter(Boolean);
  const menu = MENU_ART[segments[1] ?? ''] ?? { label: 'Application', motif: 'records' as const };
  const label = segments.includes('audit-logs') ? 'Audit Logs' : menu.label;
  return (
    <Box
      component="svg"
      viewBox="0 0 320 200"
      role="img"
      aria-label={`${label} data could not be loaded`}
      focusable="false"
      sx={{ width: compact ? 180 : 260, maxWidth: '100%', height: 'auto', flexShrink: 0, mb: 1 }}
    >
      <path d="M37 156C4 131 31 103 46 84C68 56 51 25 100 22C137 20 145 43 184 30C225 14 278 26 276 68C274 100 307 119 287 149C266 178 68 180 37 156Z" fill="#EDF4F8" />
      <ellipse cx="158" cy="179" rx="113" ry="7" fill="#DCE8EE" />
      <circle cx="34" cy="63" r="4" fill="#DCE8EE" />
      <circle cx="291" cy="100" r="3" fill="#BCD1DC" />
      <path d="M266 39V51M260 45H272M44 137V147M39 142H49" stroke="#BCD1DC" strokeWidth="3" strokeLinecap="round" />
      <g transform="translate(235 96) rotate(9 24 24)" fill="#FFF" stroke="#7A9FAB" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round">
        <MenuMotif motif={menu.motif} />
      </g>
      <rect x="69" y="54" width="167" height="113" rx="8" fill="#C7DCE5" stroke="#7D9FAC" strokeWidth="3" />
      <rect x="78" y="63" width="149" height="94" rx="3" fill="#FFF" />
      <path d="M58 167H247L239 177H66Z" fill="#AFCBD7" stroke="#7D9FAC" strokeWidth="3" strokeLinejoin="round" />
      <path d="M135 168H171" stroke="#7D9FAC" strokeWidth="3" strokeLinecap="round" />
      <path d="M151 82L179 132H123Z" fill="#FFF3F3" stroke="#CB4965" strokeWidth="4" strokeLinejoin="round" />
      <path d="M151 99V114" stroke="#CB4965" strokeWidth="4" strokeLinecap="round" />
      <circle cx="151" cy="122" r="2.5" fill="#CB4965" />
      <path d="M92 35H113M98 29H107" stroke="#D1E1E9" strokeWidth="4" strokeLinecap="round" />
    </Box>
  );
}

function RoutedIllustration({ compact }: { compact: boolean }) {
  const { pathname } = useLocation();
  return <Illustration pathname={pathname} compact={compact} />;
}

/** Menu-aware artwork with a generic fallback when rendered outside the router. */
export function ErrorIllustration({ compact = false }: { compact?: boolean }) {
  const inRouter = useInRouterContext();
  return inRouter ? <RoutedIllustration compact={compact} /> : <Illustration pathname="" compact={compact} />;
}
